import { useMemo } from "react";
import {
  Feature,
  Polygon,
  Properties,
  MultiPolygon,
  featureCollection,
  polygon,
  FeatureCollection,
} from "@turf/helpers";
import booleanIntersects from "@turf/boolean-intersects";
import { GB_OUTLINE_SIMPLE } from "src/js/util/regionOutlines";
import area from "@turf/area";
import { isDefined } from "react-migration/lib/util/isDefined";
import intersect from "@turf/intersect";
import convex from "@turf/convex";
import { SelectionGeometry } from "src/js/stores/map/store";

const convertMultiPolygonToPolygonFeatureCollection = (bounds: Feature<MultiPolygon | Polygon>) => {
  if (bounds.geometry?.type === "MultiPolygon") {
    const polygons = bounds.geometry.coordinates.map((coords) => polygon(coords, { ...bounds.properties }));
    return featureCollection(polygons, { ...bounds.properties });
  }

  return featureCollection([bounds], { ...bounds.properties }) as FeatureCollection<Polygon, Properties>;
};

type Region = "england" | "scotland" | "wales";

export const getSelectionRegions = (
  selectionBounds: Feature<SelectionGeometry> | null | undefined
): Region[] | null => {
  if (!selectionBounds || !selectionBounds.geometry?.coordinates || selectionBounds.geometry.coordinates.length === 0) {
    return null;
  }

  const geometryType = selectionBounds.geometry?.type;

  // Not supported selection type for get selected region
  if (
    geometryType === "LineString" ||
    geometryType === "MultiLineString" ||
    geometryType === "MultiPoint" ||
    geometryType === "Point"
  ) {
    return null;
  }

  const constrainedSelectionBounds = selectionBounds as Feature<Polygon | MultiPolygon, Properties>;

  try {
    // Convert to polygon as this version of turfjs doesn't support 2 multi polygon inputs to intersections
    const selectionFeatures = convertMultiPolygonToPolygonFeatureCollection(constrainedSelectionBounds);

    // Return US region here when US boundary intersects
    // Could shortcut all this

    // Find all the matching regions with the selection, boundary areas between wales and england could return two
    const matchingRegions = GB_OUTLINE_SIMPLE.features.filter((gbFeature) => {
      const selectionIntersectsRegion = selectionFeatures.features.find((selectionFeature) => {
        return booleanIntersects(selectionFeature, gbFeature);
      });

      if (selectionIntersectsRegion) {
        return gbFeature;
      }

      return null;
    });

    // Only get the unique intersections, if it intersects england twice
    const uniqueRegions = [...new Set(matchingRegions)];

    // Didn't match any of the regions
    if (uniqueRegions.length === 0) {
      return null;
    }

    return uniqueRegions.map((region) => region.properties?.country?.toLowerCase());
  } catch (error) {
    console.warn(error);
    if (!constrainedSelectionBounds || !constrainedSelectionBounds.geometry) {
      return null;
    }

    // In the event of an unknown error determining region
    // Fallback to using a simplified shape to determine region, accuracy will be lower
    // But this will reduce impact on end user
    // E.g. Error "Each LinearRing of a Polygon must have 4 or more Positions"
    const hull = convex(constrainedSelectionBounds.geometry);
    if (!hull) {
      // If the process of determining a hull isn't able to create a geometry
      // we have no way of determining the selection location
      throw new Error("Unable to determine boundary selection region");
    }

    const matchingRegions = GB_OUTLINE_SIMPLE.features
      .map((gbFeature) => intersect(hull, gbFeature, { properties: gbFeature.properties }))
      .filter(isDefined)
      .sort((a, b) => area(b) - area(a));

    const fallbackRegions = matchingRegions.map((region) => region.properties?.country?.toLowerCase());

    return fallbackRegions;
  }
};

export const useGetSelectionRegions = (
  selectionBounds: Feature<SelectionGeometry> | null | undefined
): Region[] | null => {
  return useMemo(() => {
    return getSelectionRegions(selectionBounds);
  }, [selectionBounds]);
};

export const useGetSelectionRegion = (
  selectionBounds: Feature<SelectionGeometry> | null | undefined
): Region | null => {
  return useMemo(() => {
    if (
      !selectionBounds ||
      !selectionBounds.geometry?.coordinates ||
      selectionBounds.geometry.coordinates.length === 0
    ) {
      return null;
    }

    const geometryType = selectionBounds.geometry?.type;

    // Not supported selection type for get selected region
    if (
      geometryType === "LineString" ||
      geometryType === "MultiLineString" ||
      geometryType === "MultiPoint" ||
      geometryType === "Point"
    ) {
      return null;
    }

    const constrainedSelectionBounds = selectionBounds as Feature<Polygon | MultiPolygon, Properties>;

    try {
      // Convert to polygon as this version of turfjs doesn't support 2 multi polygon inputs to intersections
      const selectionFeatures = convertMultiPolygonToPolygonFeatureCollection(constrainedSelectionBounds);

      // Return US region here when US boundary intersects
      // Could shortcut all this

      // Find all the matching regions with the selection, boundary areas between wales and england could return two
      const matchingRegions = GB_OUTLINE_SIMPLE.features.filter((gbFeature) => {
        const selectionIntersectsRegion = selectionFeatures.features.find((selectionFeature) => {
          return booleanIntersects(selectionFeature, gbFeature);
        });

        if (selectionIntersectsRegion) {
          return gbFeature;
        }

        return null;
      });

      // Only get the unique intersections, if it intersects england twice
      const uniqueRegions = [...new Set(matchingRegions)];

      // Didn't match any of the regions
      if (uniqueRegions.length === 0) {
        return null;
      }

      // Matches only one region, we can save the time determining the intersections here
      if (uniqueRegions.length === 1) {
        const country = uniqueRegions[0]?.properties?.country?.toLowerCase();

        return country;
      }

      // Find the largest intersection with the region and use this to determine the region
      const largestIntersectingRegion = uniqueRegions
        .flatMap((gbFeature) => {
          const selectionIntersectsRegion = selectionFeatures.features
            .map((selectionFeature) => {
              if (selectionFeature.geometry == null) {
                return undefined;
              }
              return intersect(selectionFeature.geometry, gbFeature, {
                properties: gbFeature.properties,
              });
            })
            .filter(isDefined);

          return selectionIntersectsRegion;
        })
        .sort((a, b) => area(b) - area(a));

      const country = largestIntersectingRegion[0]?.properties?.country?.toLowerCase();
      return country;
    } catch (error) {
      console.warn(error);
      if (!constrainedSelectionBounds || !constrainedSelectionBounds.geometry) {
        return null;
      }

      // In the event of an unknown error determining region
      // Fallback to using a simplified shape to determine region, accuracy will be lower
      // But this will reduce impact on end user
      // E.g. Error "Each LinearRing of a Polygon must have 4 or more Positions"
      const hull = convex(constrainedSelectionBounds.geometry);
      if (!hull) {
        // If the process of determining a hull isn't able to create a geometry
        // we have no way of determining the selection location
        throw new Error("Unable to determine boundary selection region");
      }

      const matchingRegions = GB_OUTLINE_SIMPLE.features
        .map((gbFeature) => intersect(hull, gbFeature, { properties: gbFeature.properties }))
        .filter(isDefined)
        .sort((a, b) => area(b) - area(a));

      const fallbackRegion = matchingRegions[0]?.properties?.country?.toLowerCase();

      return fallbackRegion;
    }
  }, [selectionBounds]);
};
